import * as React from 'react';
import {
  useState,
  useLayoutEffect,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {Card, Placeholder, Icon} from 'semantic-ui-react';
import {RunHistoryRow} from '../types/run';
import {runLink} from '../util/runhelpers';
import {RepresentationType} from '../types/libs/nglviewer';

import MessageMediaNotFound from './MessageMediaNotFound';

import memoize from 'memoize-one';
import {useLoadFile, FileInfo} from '../util/requests';
import {MediaCardProps} from './MediaCard';
import {makeCaptions, labelComponent, useStepWithRollback} from '../util/media';
import {onNextExitFullscreen} from '../util/fullscreen';
import makeComp from '../util/profiler';

export interface MoleculeConfig {
  representation?: RepresentationType;
}

export const MoleculeCard: React.FunctionComponent<MediaCardProps & {
  moleculeConfig?: MoleculeConfig;
}> = makeComp(
  props => {
    // Use async import for ngl code to split bundle
    type NGLLib = typeof import('../util/nglviewerRender');
    const [nglLib, setNglLib] = useState<NGLLib>();
    useEffect(() => {
      import('../util/nglviewerRender').then(setNglLib);
    }, []);

    const {run, mediaKey, mediaIndex, runSignature, height, width} = props;
    const stageMountRef = useRef<HTMLDivElement>(null);

    const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
      props
    );

    const mediaPath = getMedia(stepData, mediaKey, mediaIndex);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const [blobData, setBlobData] = useState<Blob>();
    const [directUrl, setDirectUrl] = useState<string>();
    const [screenshotURL, setScreenshotURL] = useState<string>();

    const onSuccess = useCallback((blob: Blob, file: FileInfo) => {
      setBlobData(blob);
      setDirectUrl(file.directUrl);
    }, []);

    const representation = props.moleculeConfig?.representation;
    const settings = useMemo(
      () => ({
        representation,
      }),
      [representation]
    );

    useLayoutEffect(() => {
      if (
        nglLib == null ||
        directUrl == null ||
        blobData == null ||
        stageMountRef.current == null
      ) {
        return;
      }
      // setFileData(file);
      // setDirectUrl(url);
      const ext = directUrl
        .split('?')[0]
        .split('.')
        .pop();

      if (ext == null) {
        throw new Error('Molecule file must have an extension');
      }
      nglLib
        .moleculeScreenshot(blobData, {width, height}, settings, ext)
        .then(imageBlob => {
          setScreenshotURL(window.URL.createObjectURL(imageBlob));
        });
    }, [blobData, directUrl, width, height, settings, nglLib]);

    const requestFullscreen = () => {
      if (
        nglLib == null ||
        directUrl == null ||
        blobData == null ||
        stageMountRef.current == null
      ) {
        return;
      }

      const screenW = window.screen.width;
      const screenH = window.screen.height;

      const ext = directUrl
        .split('?')[0]
        .split('.')
        .pop();

      if (ext == null) {
        throw new Error('Molecule file must have an extension');
      }

      const stage = nglLib.moleculeStage(
        stageMountRef.current,
        blobData,
        {width: screenW, height: screenH},
        {representation: props.moleculeConfig?.representation},
        ext
      );

      onNextExitFullscreen(() => {
        stage.dispose();
        if (stageMountRef.current) {
          stageMountRef.current.innerHTML = '';
        }
      });

      try {
        stageMountRef.current.requestFullscreen();
      } catch (e) {
        throw new Error('Fullscreen request invalid: ' + e);
      }
    };

    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will retrigger a new file download.
    useLoadFile(runSignature, mediaPath || '', {
      responseType: 'file',
      onSuccess,
      fallback: rollback,
      skip: mediaPath === '' || failedToLoad,
    });

    const currentMediaMetadata = stepData && stepData[mediaKey];

    const mediaGroupingCount =
      (currentMediaMetadata && currentMediaMetadata.grouping) || 1;

    const captions = useMemo(() => {
      return makeCaptions(currentMediaMetadata, mediaIndex, mediaGroupingCount);
    }, [currentMediaMetadata, mediaIndex, mediaGroupingCount]);

    return (
      <Card className="molecule-card media-card" style={{width, height}}>
        {labelComponent(props, currentStep, titleLink)}

        {!mediaPath ? (
          <MessageMediaNotFound
            mediaKey={mediaKey}
            stepIndex={currentStep}
            mediaIndex={mediaIndex}
            mediaType="molecule"
          />
        ) : (
          screenshotURL == null && (
            <Placeholder style={{width, height}}>
              <Placeholder.Image />
            </Placeholder>
          )
        )}
        <div ref={stageMountRef}></div>
        {screenshotURL != null && (
          <div>
            <img
              alt={`Representation of molecule ${mediaKey}`}
              style={{position: 'absolute', height: '100%'}}
              src={screenshotURL}
            />
            <div className="media-card__fullscreen" onClick={requestFullscreen}>
              <Icon size="large" link name="expand arrows alternate" />
            </div>
          </div>
        )}
        {/* CAPTIONS */}
        {captions.length > 0 && (
          <div className="image-card-caption">{captions}</div>
        )}
      </Card>
    );
  },
  {id: 'MoleculeCard'}
);

// Get the new-style Media object or null if there isn't one.
const getMedia = memoize(
  (
    stepData: RunHistoryRow | undefined,
    mediaKey: string,
    index: number
  ): string | null => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'molecule') {
      if (stepMedia.filenames && stepMedia.filenames[index]) {
        return stepMedia.filenames[index];
      } else {
        return null;
      }
    } else if (stepMedia._type === 'molecule-file') {
      return stepMedia.path;
    } else {
      return null;
    }
  }
);

export default MoleculeCard;
